import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from './components/AdminLayout';
import { digitalToolsAuth } from './utils/digitalToolsAuth';
import { shouldUseSSOAuth } from './utils/env';
import { setDigitalToolsAuth } from './utils/apiHelper';
import './App.css';

function App() {
  const [useSSOAuth, setUseSSOAuth] = useState(null);
  const [authState, setAuthState] = useState('checking');
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  const digitalToolsAuthRef = useRef(digitalToolsAuth);
  const unsubscribeRef = useRef(null);
  const initializationStartedRef = useRef(false);
  const redirectAttemptedRef = useRef(false);

  useEffect(() => {
    if (initializationStartedRef.current) {
      return;
    }
    initializationStartedRef.current = true;

    const shouldUseSSO = shouldUseSSOAuth();
    setUseSSOAuth(shouldUseSSO);
    setDigitalToolsAuth(digitalToolsAuthRef.current);

    if (!shouldUseSSO) {
      setAuthState('authenticated');
      setIsInitialized(true);
      return;
    }

    const auth = digitalToolsAuthRef.current;
    if (!auth) {
      setError(new Error('認証モジュールが初期化されていません'));
      setIsInitialized(true);
      return;
    }

    const handleAuthStateChange = (state) => {
      setAuthState(state);
    };

    unsubscribeRef.current = auth.onAuthStateChanged(handleAuthStateChange);

    (async () => {
      try {
        await auth.checkAuth();
        setIsInitialized(true);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsInitialized(true);
      }
    })();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!useSSOAuth || authState !== 'unauthenticated' || !isInitialized) {
      return;
    }
    if (redirectAttemptedRef.current) {
      return;
    }
    redirectAttemptedRef.current = true;

    const auth = digitalToolsAuthRef.current;
    if (!auth) {
      return;
    }

    const timer = setTimeout(() => {
      auth.redirectToDigitalTools();
    }, 1000);
    return () => clearTimeout(timer);
  }, [useSSOAuth, authState, isInitialized]);

  if (error) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-error">認証エラーが発生しました</div>
        <div className="auth-gate-detail">{error.message}</div>
        <button type="button" className="auth-gate-button" onClick={() => window.location.reload()}>
          再読み込み
        </button>
      </div>
    );
  }

  if (useSSOAuth === null) {
    return (
      <div className="auth-gate">
        <div>初期化中...</div>
      </div>
    );
  }

  if (useSSOAuth && (!isInitialized || authState === 'checking')) {
    return (
      <div className="auth-gate">
        <div>認証チェック中...</div>
      </div>
    );
  }

  if (useSSOAuth && authState === 'unauthenticated') {
    return (
      <div className="auth-gate">
        <div>認証が必要です。デジタルツールにリダイレクトしています...</div>
      </div>
    );
  }

  return <AdminLayout />;
}

export default App;
